import { invoke } from '@tauri-apps/api/core';
import type { SqlDriver, SqlValue, Statement } from './driver';

/**
 * Native SQLite in the desktop app (rusqlite, bundled with FTS5). The database lives
 * in the OS app-data folder, so it is never evicted like browser storage can be.
 * Blobs cross the IPC boundary as base64 strings tagged with `{ $blob }`.
 */
type Wire = string | number | null | { $blob: string };

function toWire(v: SqlValue): Wire {
  if (v instanceof Uint8Array) {
    let s = '';
    for (const b of v) s += String.fromCharCode(b);
    return { $blob: btoa(s) };
  }
  return v;
}

function fromWire(v: Wire): SqlValue {
  if (v !== null && typeof v === 'object') {
    const bin = atob(v.$blob);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  return v;
}

export async function createTauriDriver(): Promise<SqlDriver> {
  await invoke('db_open');
  return {
    persistent: true,
    query: async <T>(sql: string, params: SqlValue[] = []) => {
      const rows = await invoke<Record<string, Wire>[]>('db_query', {
        sql,
        params: params.map(toWire),
      });
      return rows.map(
        (r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, fromWire(v)])) as T,
      );
    },
    batch: async (statements: Statement[]) => {
      await invoke('db_batch', {
        statements: statements.map((s) => ({ sql: s.sql, params: (s.params ?? []).map(toWire) })),
      });
    },
    close: async () => {},
  };
}

export function isTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}
